import React, { useEffect, useRef, useState } from 'react'
import Quill from 'quill'
// Quill CSS is loaded via CDN in the admin layout

// Register ComponentBlot only once
let componentBlotRegistered = false

function WysiwygEditor({ 
  initialContent = '', 
  hiddenFieldId, 
  placeholder = 'Start writing...', 
  height = '300px',
  onImageUpload 
}) {
  
  // Decode HTML entities
  const decodeHtmlEntities = (html) => {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = html
    return textarea.value
  }
  
  const decodedContent = initialContent ? decodeHtmlEntities(initialContent) : ''
  const editorRef = useRef(null)
  const quillRef = useRef(null)
  const [isReady, setIsReady] = useState(false)
  const [isSourceMode, setIsSourceMode] = useState(false)
  const [sourceContent, setSourceContent] = useState(decodedContent)
  const [showHtmlInsert, setShowHtmlInsert] = useState(false)
  const [htmlToInsert, setHtmlToInsert] = useState('')

  // Add global handler for component removal
  useEffect(() => {
    const handleComponentRemove = (e) => {
      if (e.target.classList.contains('ql-component-remove') || e.target.parentElement?.classList.contains('ql-component-remove')) {
        e.preventDefault()
        e.stopPropagation()
        
        const button = e.target.classList.contains('ql-component-remove') ? e.target : e.target.parentElement
        const componentDiv = button.closest('.ql-custom-component')
        
        if (componentDiv && quillRef.current) {
          // Remove the component
          componentDiv.remove()
          
          // Update Quill's content
          const hiddenField = document.getElementById(hiddenFieldId)
          if (hiddenField) {
            hiddenField.value = quillRef.current.root.innerHTML
          }
        }
      }
    }
    
    document.addEventListener('click', handleComponentRemove)
    return () => document.removeEventListener('click', handleComponentRemove)
  }, [hiddenFieldId])

  useEffect(() => {
    if (!editorRef.current || quillRef.current || isSourceMode) return

    // Custom image handler
    const imageHandler = () => {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.setAttribute('accept', 'image/*')
      input.click()

      input.onchange = async () => {
        const file = input.files[0]
        if (file) {
          if (onImageUpload) {
            // If a custom upload handler is provided, use it
            const imageUrl = await onImageUpload(file)
            if (imageUrl) {
              const range = quillRef.current.getSelection()
              quillRef.current.insertEmbed(range.index, 'image', imageUrl)
            }
          } else {
            // Default: convert to base64
            const reader = new FileReader()
            reader.onload = (e) => {
              const range = quillRef.current.getSelection()
              quillRef.current.insertEmbed(range.index, 'image', e.target.result)
            }
            reader.readAsDataURL(file)
          }
        }
      }
    }

    // Configure Quill to preserve custom elements
    if (!componentBlotRegistered) {
      const BlockEmbed = Quill.import('blots/block/embed')
      
      // Custom blot for our components
      class ComponentBlot extends BlockEmbed {
        static create(value) {
          const node = super.create()
          node.innerHTML = value
          return node
        }

        static value(node) {
          return node.innerHTML
        }
      }
      ComponentBlot.blotName = 'component'
      ComponentBlot.tagName = 'div'
      ComponentBlot.className = 'ql-custom-component'
      
      Quill.register(ComponentBlot)
      componentBlotRegistered = true
    }
    
    // Initialize Quill
    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      placeholder: placeholder,
      modules: {
        toolbar: {
          container: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['link', 'image'],
            ['clean']
          ],
          handlers: {
            image: imageHandler
          }
        }
      }
    })

    quillRef.current = quill

    // Set initial content
    if (decodedContent) {
      quill.root.innerHTML = decodedContent
      
      // Add remove buttons to any existing custom components
      const existingComponents = quill.root.querySelectorAll('.ql-custom-component')
      existingComponents.forEach(component => {
        if (!component.querySelector('.ql-component-remove')) {
          const removeButton = document.createElement('button')
          removeButton.type = 'button'
          removeButton.className = 'ql-component-remove'
          removeButton.title = 'Remove component'
          removeButton.innerHTML = `
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          `
          component.appendChild(removeButton)
        }
      })
    }

    // Update hidden field on content change
    const hiddenField = document.getElementById(hiddenFieldId)
    if (hiddenField) {
      // Set initial value
      hiddenField.value = decodedContent || ''
      
      quill.on('text-change', () => {
        hiddenField.value = quill.root.innerHTML
      })
    }

    // Store quill instance on the element for external access
    if (editorRef.current.parentElement.parentElement) {
      editorRef.current.parentElement.parentElement._quillInstance = quill
    }
    // Also store on the actual custom element if it exists
    const customElement = editorRef.current.closest('nfca-admin-wysiwyg-editor, nfca-admin-wysiwyg-with-upload')
    if (customElement) {
      customElement._quillInstance = quill
    }

    setIsReady(true)

    // Cleanup
    return () => {
      if (quillRef.current) {
        quillRef.current = null
      }
    }
  }, [decodedContent, hiddenFieldId, placeholder, onImageUpload, isSourceMode])

  const toggleSourceMode = () => {
    if (isSourceMode) {
      // Switching from source to visual mode
      if (quillRef.current) {
        quillRef.current.root.innerHTML = sourceContent
        const hiddenField = document.getElementById(hiddenFieldId)
        if (hiddenField) {
          hiddenField.value = sourceContent
        }
      }
    } else {
      // Switching from visual to source mode
      if (quillRef.current) {
        setSourceContent(quillRef.current.root.innerHTML)
      }
    }
    setIsSourceMode(!isSourceMode)
  }

  const handleSourceChange = (e) => {
    setSourceContent(e.target.value)
    const hiddenField = document.getElementById(hiddenFieldId)
    if (hiddenField) {
      hiddenField.value = e.target.value
    }
  }

  const insertHtmlFragment = () => {
    if (quillRef.current && htmlToInsert.trim()) {
      const range = quillRef.current.getSelection()
      let insertIndex = range ? range.index : quillRef.current.getLength() - 1
      
      // Check if we're inserting a custom component
      const isCustomComponent = htmlToInsert.trim().toLowerCase().startsWith('<nfca-')
      
      if (isCustomComponent) {
        // Extract component name for display
        const componentMatch = htmlToInsert.match(/<(nfca-[^>\s]+)/i)
        const componentName = componentMatch ? componentMatch[1] : 'custom'
        
        // Wrap component in a div for Quill to handle properly with remove button
        const wrappedHtml = `<div class="ql-custom-component" contenteditable="false" data-component-type="${componentName}">
          ${htmlToInsert}
          <button type="button" class="ql-component-remove" title="Remove component">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div><p><br></p>`
        quillRef.current.clipboard.dangerouslyPasteHTML(insertIndex, wrappedHtml)
      } else {
        // Insert regular HTML
        quillRef.current.clipboard.dangerouslyPasteHTML(insertIndex, htmlToInsert)
      }
      
      // Wait for DOM to update and components to render
      setTimeout(() => {
        // Find the inserted element
        const editorElement = quillRef.current.root
        let insertedElement = null
        
        if (isCustomComponent) {
          // Look for the custom component wrapper
          const customComponents = editorElement.querySelectorAll('.ql-custom-component')
          insertedElement = customComponents[customComponents.length - 1]
        } else {
          // Look for other inserted elements
          const allElements = editorElement.querySelectorAll('*')
          for (let i = allElements.length - 1; i >= 0; i--) {
            const el = allElements[i]
            if (el.tagName === 'IFRAME' || el.innerHTML.includes(htmlToInsert)) {
              insertedElement = el
              break
            }
          }
        }
        
        // Scroll the element into view if found
        if (insertedElement) {
          const editorContainer = editorRef.current.parentElement
          const containerRect = editorContainer.getBoundingClientRect()
          const elementRect = insertedElement.getBoundingClientRect()
          
          // Check if element is below the visible area
          if (elementRect.bottom > containerRect.bottom || elementRect.top < containerRect.top) {
            insertedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
        
        // Move cursor after inserted content
        const newLength = quillRef.current.getLength()
        quillRef.current.setSelection(newLength - 1)
        
        // Try to mount any new React components
        // Remount scans the DOM and mounts any unmounted custom elements
        if (insertedElement && isCustomComponent) {
          try {
            // Dispatch a custom event that could trigger remounting
            const event = new CustomEvent('remount:update', { 
              bubbles: true,
              detail: { element: insertedElement }
            })
            document.dispatchEvent(event)
            
            // Also try manual component initialization by re-importing
            // This forces remount to scan for new elements
            import('../../index.js').then(() => {
              console.log('Components re-initialized')
            })
          } catch (e) {
            console.log('Component mounting handled by remount')
          }
        }
      }, 200)
      
      // Clear and close dialog
      setHtmlToInsert('')
      setShowHtmlInsert(false)
      
      // Update hidden field
      const hiddenField = document.getElementById(hiddenFieldId)
      if (hiddenField) {
        hiddenField.value = quillRef.current.root.innerHTML
      }
    }
  }

  const commonHtmlFragments = [
    { label: 'Gift Voucher Form', html: '<nfca-add-gift-voucher></nfca-add-gift-voucher>' },
    { label: 'Event Booking', html: '<nfca-event-booking event-data="" instance-data=""></nfca-event-booking>' },
    { label: 'What\'s On List', html: '<nfca-whatson></nfca-whatson>' },
    { label: 'Slideshow', html: '<nfca-slideshow></nfca-slideshow>' },
    { label: 'Line Break', html: '<br>' },
    { label: 'Horizontal Rule', html: '<hr>' },
    { label: 'YouTube Video', html: '<iframe width="560" height="315" src="https://www.youtube.com/embed/VIDEO_ID" frameborder="0" allowfullscreen></iframe>' },
    { label: 'Custom Section', html: '<section class="my-4">\n  <h2>Section Title</h2>\n  <p>Section content goes here.</p>\n</section>' }
  ]

  return (
    <div className="wysiwyg-editor relative">
      <style>{`
        .ql-custom-component {
          background-color: #f3f4f6;
          border: 2px dashed #9ca3af;
          border-radius: 0.5rem;
          padding: 1rem;
          margin: 1rem 0;
          position: relative;
          min-height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .dark .ql-custom-component {
          background-color: #374151;
          border-color: #6b7280;
        }
        .ql-custom-component::before {
          content: 'Component: ' attr(data-component-type);
          position: absolute;
          top: 0;
          left: 0;
          background-color: #3b82f6;
          color: white;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          border-radius: 0.25rem 0 0.5rem 0;
        }
        .ql-custom-component .ql-component-remove {
          position: absolute;
          top: 0.25rem;
          right: 0.25rem;
          background-color: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 1.5rem;
          height: 1.5rem;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          z-index: 10;
        }
        .ql-custom-component .ql-component-remove svg {
          width: 0.875rem;
          height: 0.875rem;
        }
        .ql-custom-component .ql-component-remove:hover {
          background-color: #dc2626;
        }
        .dark .ql-custom-component .ql-component-remove {
          background-color: #dc2626;
        }
        .dark .ql-custom-component .ql-component-remove:hover {
          background-color: #b91c1c;
        }
        .ql-editor {
          overflow-y: auto;
        }
      `}</style>
      <div className="flex justify-end mb-2 space-x-2">
        {!isSourceMode && (
          <button
            type="button"
            onClick={() => setShowHtmlInsert(true)}
            className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Insert HTML
          </button>
        )}
        <button
          type="button"
          onClick={toggleSourceMode}
          className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {isSourceMode ? (
            <>
              <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Visual Editor
            </>
          ) : (
            <>
              <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              HTML Source
            </>
          )}
        </button>
      </div>
      
      {isSourceMode ? (
        <textarea
          value={sourceContent}
          onChange={handleSourceChange}
          style={{ height: height }}
          className="w-full p-4 font-mono text-sm bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded"
          placeholder="Enter HTML code here..."
        />
      ) : (
        <div 
          ref={editorRef} 
          style={{ height: height }}
          className="bg-white dark:bg-gray-700 overflow-y-auto"
        />
      )}
      
      {/* HTML Insert Dialog */}
      {showHtmlInsert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowHtmlInsert(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Insert HTML Fragment</h3>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-grow">
              {/* Common HTML Fragments */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Insert:</p>
                <div className="grid grid-cols-2 gap-2">
                  {commonHtmlFragments.map((fragment, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setHtmlToInsert(fragment.html)}
                      className="text-left px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {fragment.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* HTML Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  HTML Code:
                </label>
                <textarea
                  value={htmlToInsert}
                  onChange={(e) => setHtmlToInsert(e.target.value)}
                  rows={6}
                  className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter or paste HTML code here..."
                />
              </div>
              
              {/* Preview */}
              {htmlToInsert && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</p>
                  <div 
                    className="p-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded max-h-48 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: htmlToInsert }}
                  />
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setHtmlToInsert('')
                  setShowHtmlInsert(false)
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={insertHtmlFragment}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                disabled={!htmlToInsert.trim()}
              >
                Insert HTML
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WysiwygEditor